import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { take } from 'rxjs';

@Component({
    selector: 'app-change-password-modal',
    imports: [
    A11yModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule
],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './change-password-modal.component.html',
    styleUrls: ['./change-password-modal.component.scss']
})
export class ChangePasswordModalComponent {
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private dialogRef = inject(MatDialogRef<ChangePasswordModalComponent>);
  private authService = inject(AuthService);
  private snackbar = inject(MatSnackBar);
  private router = inject(Router);
  public data: any = inject(MAT_DIALOG_DATA, { optional: true });

  isForced = this.data?.isForced ?? false;

  passwordForm: FormGroup = this.fb.group({
    currentPassword: ['', Validators.required],
    newPassword: [
      '', 
      [
        Validators.required, 
        Validators.minLength(8),
        Validators.pattern(/^(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).*$/)
      ]
    ],
    confirmPassword: ['', Validators.required]
  }, { validators: this.passwordMatchValidator });

  hideCurrent = true;
  hideNew = true;
  hideConfirm = true;
  isLoading = false;

  get newPasswordValue(): string {
    return this.passwordForm.get('newPassword')?.value || '';
  }

  get confirmPasswordValue(): string {
    return this.passwordForm.get('confirmPassword')?.value || '';
  }

  get hasMinLength(): boolean {
    return this.newPasswordValue.length >= 8;
  }

  get hasSpecialChar(): boolean {
    return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.newPasswordValue);
  }

  get passwordsMatch(): boolean {
    return this.newPasswordValue.length > 0 && this.newPasswordValue === this.confirmPasswordValue;
  }

  passwordMatchValidator(g: FormGroup) {
    const newPass = g.get('newPassword')?.value;
    const confirmPass = g.get('confirmPassword')?.value;
    return newPass === confirmPass ? null : { mismatch: true };
  }

  onSubmit() {
    if (this.passwordForm.invalid) return;

    this.isLoading = true;
    const body = this.passwordForm.value as { currentPassword: string; newPassword: string };
    this.authService.changePassword(body).pipe(take(1)).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.cdr.markForCheck();
        if (this.isForced) {
          sessionStorage.clear();
          this.snackbar.open('Senha alterada! Por favor, faça login com sua nova senha.', 'Fechar', { duration: 5000 });
          this.router.navigate(['/login']);
        } else {
          this.snackbar.open(res.message || 'Senha alterada com sucesso!', 'Fechar', { duration: 3000 });
        }
        this.dialogRef.close(true);
      },
      error: (err: any) => {
        this.isLoading = false;
        this.cdr.markForCheck();
        this.snackbar.open(err.error?.errors?.[0]?.messageCustomer || 'Falha ao alterar senha', 'Fechar', { duration: 3000, panelClass: ['error-snackbar'] });
      }
    });
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
